import React, { useEffect, useState, useRef } from 'react';
import {
	TrashIcon,
	PlusIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	PhotoIcon,
	CheckCircleIcon,
	ClockIcon,
	XCircleIcon,
	DocumentDuplicateIcon,
	ClipboardDocumentCheckIcon,
	PencilSquareIcon,
} from '@heroicons/react/24/outline';

import {
	createSop,
	updateSop,
	deleteSop,
	getSopLatestVersion,
	uploadStepAttachment,
} from '../api';
import { StackableModal, FilterDropdown } from '../components/nextgen';
import { classNames } from '../components/helpers/functions';
import { prepareForEdit } from '../components/helpers/markdown';
import MarkdownEditor from '../components/MarkdownEditor';
import MarkdownOrHtmlDisplay from '../components/MarkdownOrHtmlDisplay';
import { uploadSopImage } from '../api/sops';
import ManufacturerModelSelect from '../components/ManufacturerModelSelect';
import SopVersionHistory from '../components/SopVersionHistory';
import SopVersionDiff from '../components/SopVersionDiff';
import SopAuditLog from '../components/SopAuditLog';
import StepAttachments from '../components/StepAttachments';

import useModal from '../hooks/useModal';

const MODAL_NAME = 'editSop';

// SOP Categories
const CATEGORIES = [
	{ value: 'equipment', label: 'Equipment' },
	{ value: 'process', label: 'Process' },
	{ value: 'safety', label: 'Safety' },
];

// SOP Statuses
const STATUS_CONFIG = {
	draft: {
		label: 'Draft',
		color: 'bg-yellow-100 text-yellow-800',
		icon: ClockIcon,
	},
	approved: {
		label: 'Latest',
		color: 'bg-green-100 text-green-800',
		icon: CheckCircleIcon,
	},
	deprecated: {
		label: 'Deprecated',
		color: 'bg-red-100 text-red-800',
		icon: XCircleIcon,
	},
};

// Extract first line of text from HTML or markdown content
const getFirstLine = (content) => {
	if (!content) return '';
	// Replace block elements with newlines (for HTML), then strip remaining tags
	const text = content
		.replace(/<\/?(p|div|br|h[1-6])[^>]*>/gi, '\n')
		.replace(/<[^>]*>/g, '')
		.replace(/^#+\s*/gm, '') // Remove markdown headings
		.replace(/\*\*|__/g, '') // Remove bold markers
		.replace(/\*|_/g, '') // Remove italic markers
		.replace(/!\[.*?\]\(.*?\)/g, '') // Remove markdown images
		.trim();
	// Get first non-empty line
	const firstLine = text.split('\n').find((line) => line.trim()) || '';
	// Truncate if needed
	const truncated = firstLine.trim().slice(0, 60);
	return truncated + (firstLine.length > 60 ? '...' : '');
};

// Check if content contains an image (HTML or markdown)
const hasImage = (content) => {
	if (!content) return false;
	// Check for HTML img tag or markdown image syntax
	return /<img\s/i.test(content) || /!\[.*?\]\(.*?\)/.test(content);
};

// Helper to format version string
const formatVersion = (major, minor) => {
	return `${major || 0}.${minor || 0}`;
};

// Helper to compare arrays
const arraysEqual = (arr1, arr2) => {
	if (!arr1 && !arr2) return true;
	if (!arr1 || !arr2) return false;
	if (arr1.length !== arr2.length) return false;
	return arr1.every((item, idx) => {
		if (typeof item === 'object') {
			return JSON.stringify(item) === JSON.stringify(arr2[idx]);
		}
		return item === arr2[idx];
	});
};

// Helper to compare two form objects for equality
const formsAreEqual = (form1, form2) => {
	if (!form1 || !form2) return false;

	// Compare simple fields
	if (form1.name !== form2.name) return false;
	if (form1.description !== form2.description) return false;
	if (form1.category !== form2.category) return false;

	if (!arraysEqual(form1.steps, form2.steps)) return false;
	if (!arraysEqual(form1.manufacturerIds, form2.manufacturerIds)) return false;
	if (!arraysEqual(form1.modelIds, form2.modelIds)) return false;

	return true;
};

// Process step attachments - upload new files and return cleaned steps
const processStepAttachments = async (steps) => {
	const processedSteps = [];

	for (const step of steps) {
		const processedAttachments = [];

		for (const attachment of step.attachments || []) {
			if (attachment.isNew && attachment.file) {
				// Upload new file - creates document record in backend
				try {
					const result = await uploadStepAttachment(attachment.file);
					processedAttachments.push({
						id: result.id,
						documentId: result.documentId || result.id, // Reference to document in /documents API
						name: attachment.name,
						size: attachment.size,
						type: attachment.type,
						url: result.url,
					});
				} catch (error) {
					console.error('Failed to upload attachment:', error);
					// Skip failed uploads
				}
			} else if (!attachment.isNew) {
				// Keep existing attachments (remove previewUrl for serialization)
				const { previewUrl, ...rest } = attachment;
				processedAttachments.push(rest);
			}
		}

		processedSteps.push({
			...step,
			attachments: processedAttachments,
		});
	}

	return processedSteps;
};

// Helper to compare form with a published version
const formDiffersFromPublished = (form, publishedVersion) => {
	if (!publishedVersion) return true; // No published version = can publish

	if (form.name !== publishedVersion.name) return true;
	if ((form.description || '') !== (publishedVersion.description || ''))
		return true;
	if ((form.category || null) !== (publishedVersion.category || null))
		return true;
	if (!arraysEqual(form.steps, publishedVersion.steps || [])) return true;
	if (!arraysEqual(form.manufacturerIds, publishedVersion.manufacturers || []))
		return true;
	if (!arraysEqual(form.modelIds, publishedVersion.manufacturerModels || []))
		return true;

	return false;
};

const EditSopModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openPublishModal } = useModal('publishSop');
	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');
	const nameInputRef = useRef(null);

	const [form, setForm] = useState({
		name: '',
		description: '',
		steps: [],
		category: null,
		manufacturerIds: [],
		modelIds: [],
	});
	const [originalForm, setOriginalForm] = useState(null); // Track original state for change detection
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [expandedSteps, setExpandedSteps] = useState({});
	const [activeTab, setActiveTab] = useState('edit'); // 'edit' | 'history' | 'audit'
	const [compareVersions, setCompareVersions] = useState(null); // { v1, v2 }
	const [viewingVersion, setViewingVersion] = useState(null);
	const [latestPublishedVersion, setLatestPublishedVersion] = useState(null);

	const isEditing = !!open?.sop?.id;
	const hasChanges = originalForm ? !formsAreEqual(form, originalForm) : false;
	const canPublish = formDiffersFromPublished(form, latestPublishedVersion);
	const currentStatus = open?.sop?.status || 'draft';
	// Use latest published version data when available
	const publishedTitle = latestPublishedVersion?.name || open?.sop?.name;
	const publishedVersionMajor =
		latestPublishedVersion?.versionMajor ?? open?.sop?.versionMajor ?? 0;
	const publishedVersionMinor =
		latestPublishedVersion?.versionMinor ?? open?.sop?.versionMinor ?? 0;
	const publishedVersionStr = formatVersion(
		publishedVersionMajor,
		publishedVersionMinor,
	);
	const isPublished = publishedVersionMajor > 0 || publishedVersionMinor > 0;

	useEffect(() => {
		if (open?.sop) {
			// Convert HTML to markdown for editing (if needed)
			const convertedSteps = (open.sop.steps || []).map((step) => ({
				...step,
				content: prepareForEdit(step.content),
			}));
			const initialForm = {
				name: open.sop.name || '',
				description: prepareForEdit(open.sop.description || ''),
				steps: convertedSteps,
				category: open.sop.category || null,
				manufacturerIds: open.sop.manufacturers?.map((m) => m.id) || [],
				modelIds: open.sop.models?.map((m) => m.id) || [],
			};
			setForm(initialForm);
			setOriginalForm(initialForm);
			setExpandedSteps({});

			// Fetch the latest published version if one exists
			if (
				open.sop.id &&
				(open.sop.versionMajor > 0 || open.sop.versionMinor > 0)
			) {
				getSopLatestVersion({ id: open.sop.id })
					.then((version) => setLatestPublishedVersion(version))
					.catch((err) => {
						console.error('Failed to fetch latest version:', err);
						setLatestPublishedVersion(null);
					});
			} else {
				setLatestPublishedVersion(null);
			}
		} else if (open) {
			const initialForm = {
				name: '',
				description: '',
				steps: [],
				category: null,
				manufacturerIds: [],
				modelIds: [],
			};
			setForm(initialForm);
			setOriginalForm(initialForm);
			setExpandedSteps({});
			setLatestPublishedVersion(null);
		}
	}, [open]);

	const handleClose = () => {
		setForm({
			name: '',
			description: '',
			steps: [],
			category: null,
			manufacturerIds: [],
			modelIds: [],
		});
		setExpandedSteps({});
		setActiveTab('edit');
		setCompareVersions(null);
		setViewingVersion(null);
		setLatestPublishedVersion(null);
		setClose();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.name.trim()) return;

		setIsSubmitting(true);

		try {
			// Process step attachments (upload new files)
			const processedSteps = await processStepAttachments(form.steps);

			if (isEditing) {
				await updateSop({
					id: open.sop.id,
					name: form.name,
					description: form.description,
					steps: processedSteps,
					category: form.category,
					manufacturerIds: form.manufacturerIds,
					modelIds: form.modelIds,
				});
			} else {
				await createSop({
					name: form.name,
					description: form.description,
					steps: processedSteps,
					category: form.category,
					manufacturerIds: form.manufacturerIds,
					modelIds: form.modelIds,
				});
			}

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to save SOP:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = () => {
		if (!open?.sop?.id) return;

		openDeleteModal({
			title: 'Delete SOP',
			description: `Are you sure you want to delete "${form.name}"? This action cannot be undone.`,
			confirmText: 'Delete SOP',
			onConfirm: async () => {
				try {
					await deleteSop({ id: open.sop.id });
					closeDeleteModal();
					if (typeof open?.onSuccess === 'function') {
						open.onSuccess();
					}
					handleClose();
				} catch (error) {
					console.error('Failed to delete SOP:', error);
				}
			},
		});
	};

	const handlePublish = async () => {
		if (!open?.sop?.id) return;

		setIsSubmitting(true);

		try {
			// Process step attachments (upload new files)
			const processedSteps = await processStepAttachments(form.steps);

			// Save the draft first
			await updateSop({
				id: open.sop.id,
				name: form.name,
				description: form.description,
				steps: processedSteps,
				category: form.category,
				manufacturerIds: form.manufacturerIds,
				modelIds: form.modelIds,
			});

			// Update originalForm to reflect saved state
			setOriginalForm({ ...form, steps: processedSteps });

			// Open the publish modal with the updated SOP data
			openPublishModal({
				sop: {
					...open.sop,
					name: form.name,
					description: form.description,
					steps: processedSteps,
					category: form.category,
				},
				onSuccess: () => {
					if (typeof open?.onSuccess === 'function') {
						open.onSuccess();
					}
					handleClose();
				},
			});
		} catch (error) {
			console.error('Failed to save SOP before publish:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const addStep = () => {
		const newIndex = form.steps.length;
		setForm({
			...form,
			steps: [
				...form.steps,
				{
					content: '',
					sortOrder: newIndex,
					attachments: [],
					requiresInput: false,
				},
			],
		});
		// Auto-expand newly added step
		setExpandedSteps((prev) => ({ ...prev, [newIndex]: true }));
	};

	const toggleStepExpanded = (index) => {
		setExpandedSteps((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const updateStep = (index, content) => {
		const newSteps = [...form.steps];
		newSteps[index] = { ...newSteps[index], content };
		setForm({ ...form, steps: newSteps });
	};

	const updateStepAttachments = (index, attachments) => {
		const newSteps = [...form.steps];
		newSteps[index] = { ...newSteps[index], attachments };
		setForm({ ...form, steps: newSteps });
	};

	const toggleStepRequiresInput = (index) => {
		const newSteps = [...form.steps];
		newSteps[index] = {
			...newSteps[index],
			requiresInput: !newSteps[index].requiresInput,
		};
		setForm({ ...form, steps: newSteps });
	};

	const removeStep = (index) => {
		const newSteps = form.steps.filter((_, i) => i !== index);
		// Update sort orders
		const reorderedSteps = newSteps.map((step, i) => ({
			...step,
			sortOrder: i,
		}));
		setForm({ ...form, steps: reorderedSteps });
	};

	// Drag and drop handlers
	const handleDragStart = (e, index) => {
		setDraggedIndex(index);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/html', e.target.outerHTML);
	};

	const handleDragOver = (e, index) => {
		e.preventDefault();
		if (draggedIndex === null || draggedIndex === index) return;

		const newSteps = [...form.steps];
		const draggedStep = newSteps[draggedIndex];
		newSteps.splice(draggedIndex, 1);
		newSteps.splice(index, 0, draggedStep);

		// Update sort orders
		const reorderedSteps = newSteps.map((step, i) => ({
			...step,
			sortOrder: i,
		}));

		setForm({ ...form, steps: reorderedSteps });
		setDraggedIndex(index);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
	};

	const handleViewLatestPublished = () => {
		if (latestPublishedVersion) {
			setActiveTab('history');
			setViewingVersion(latestPublishedVersion);
		}
	};

	const StatusIcon = STATUS_CONFIG[currentStatus]?.icon || ClockIcon;

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={isEditing ? 'Edit Standard Operating Procedure' : 'Create SOP'}
			size="4xl"
			initialFocusRef={nameInputRef}
		>
			{isPublished ? (
				<div
					onClick={handleViewLatestPublished}
					class=" border-gray-200 rounded-lg p-2 mb-6 cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors"
				>
					<div class="flex items-center justify-between">
						<div class="flex items-center gap-3">
							<div class="p-2 bg-gray-100 rounded-lg">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									stroke-width="1.5"
									stroke="currentColor"
									aria-hidden="true"
									data-slot="icon"
									class="w-5 h-5 text-gray-600"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
									></path>
								</svg>
							</div>
							<div>
								<div class="">
									<p class="text-gray-900 font-medium text-sm">
										{`Latest Published: ${publishedTitle}`}
									</p>
									<p class="text-gray-400 text-xs mt-0.5">
										<span class="text-xs text-gray-500">
											Version {publishedVersionStr}
										</span>
									</p>
								</div>
							</div>
						</div>
						<div className="flex items-center text-gray-500 font-semibold text-xs">
							<span
								className={classNames(
									'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
									STATUS_CONFIG[currentStatus]?.color ||
										'bg-gray-100 text-gray-800',
								)}
							>
								<StatusIcon className="w-3.5 h-3.5" />
								{STATUS_CONFIG[currentStatus]?.label || currentStatus}
							</span>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								stroke-width="1.5"
								stroke="currentColor"
								aria-hidden="true"
								data-slot="icon"
								class="w-5 h-5 text-gray-400 ml-3"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="m8.25 4.5 7.5 7.5-7.5 7.5"
								></path>
							</svg>
						</div>
					</div>
				</div>
			) : (
				<div className="flex items-center gap-4 mb-6">
					<span className="text-sm text-gray-400 italic">
						Not yet published
					</span>
				</div>
			)}

			{/* Tabs (only when editing) */}
			{isEditing && (
				<div className="flex border-b border-gray-200 mb-6">
					<button
						type="button"
						onClick={() => {
							setActiveTab('edit');
							setCompareVersions(null);
							setViewingVersion(null);
						}}
						className={classNames(
							'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
							activeTab === 'edit'
								? 'border-indigo-500 text-indigo-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
						)}
					>
						Edit
					</button>
					<button
						type="button"
						onClick={() => {
							setActiveTab('history');
							setCompareVersions(null);
							setViewingVersion(null);
						}}
						className={classNames(
							'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5',
							activeTab === 'history'
								? 'border-indigo-500 text-indigo-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
						)}
					>
						<DocumentDuplicateIcon className="h-4 w-4" />
						Version History
						{isPublished && (
							<span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
								v{publishedVersionStr}
							</span>
						)}
					</button>
					<button
						type="button"
						onClick={() => {
							setActiveTab('audit');
							setCompareVersions(null);
							setViewingVersion(null);
						}}
						className={classNames(
							'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5',
							activeTab === 'audit'
								? 'border-indigo-500 text-indigo-600'
								: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
						)}
					>
						<ClipboardDocumentCheckIcon className="h-4 w-4" />
						Audit Log
					</button>
				</div>
			)}

			{/* Version History Tab Content */}
			{activeTab === 'history' && isEditing && (
				<div className="pb-6">
					{compareVersions ? (
						<SopVersionDiff
							sopId={open?.sop?.id}
							version1={compareVersions.v1}
							version2={compareVersions.v2}
							onBack={() => setCompareVersions(null)}
						/>
					) : (
						<SopVersionHistory
							sopId={open?.sop?.id}
							onViewVersion={(version) => setViewingVersion(version)}
							onCompareVersions={(v1, v2) => setCompareVersions({ v1, v2 })}
						/>
					)}
				</div>
			)}

			{/* Audit Log Tab Content */}
			{activeTab === 'audit' && isEditing && (
				<div className="pb-6">
					<SopAuditLog sopId={open?.sop?.id} />
				</div>
			)}

			{/* Edit Tab Content */}
			{activeTab === 'edit' && (
				<form onSubmit={handleSubmit} className="pb-6">
					{/* Unpublished changes notification */}
					{isPublished && canPublish && (
						<div className="mb-4 rounded-md bg-amber-50 border border-amber-200 p-3">
							<div className="flex items-center">
								<div className="flex-shrink-0">
									<svg
										className="h-5 w-5 text-amber-400"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="ml-3">
									<p className="text-sm text-amber-700">
										This draft has unpublished changes. Click{' '}
										<span className="font-medium">Approve Version</span> to
										create a new version.
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Template Name */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Template Name <span className="text-red-500">*</span>
						</label>
						<input
							ref={nameInputRef}
							type="text"
							value={form.name}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
							placeholder="Enter template name"
							className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
							required
						/>
					</div>

					{/* Description */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Description
						</label>
						<MarkdownEditor
							value={form.description}
							onChange={(content) => setForm({ ...form, description: content })}
							placeholder="Enter an optional description for this SOP..."
							uploadImage={uploadSopImage}
						/>
					</div>

					{/* Category */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Category
						</label>
						<FilterDropdown
							label="Category"
							placeholder="Select a category..."
							value={form.category}
							options={CATEGORIES}
							onChange={(value) =>
								setForm({ ...form, category: value === 'all' ? null : value })
							}
							clearable={true}
						/>
					</div>

					{/* Equipment-specific: Manufacturer and Model Selection */}
					{form.category === 'equipment' && (
						<div className="mb-6">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Equipment Applicability
							</label>
							<ManufacturerModelSelect
								manufacturerIds={form.manufacturerIds}
								modelIds={form.modelIds}
								onManufacturerChange={(ids) =>
									setForm({ ...form, manufacturerIds: ids })
								}
								onModelChange={(ids) => setForm({ ...form, modelIds: ids })}
							/>
						</div>
					)}

					{/* Checklist Steps */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-3">
							Checklist Steps
						</label>

						<div className="space-y-3">
							{form.steps.map((step, index) => (
								<div
									key={index}
									draggable={!expandedSteps[index]}
									onDragStart={(e) => handleDragStart(e, index)}
									onDragOver={(e) => handleDragOver(e, index)}
									onDragEnd={handleDragEnd}
									className={classNames(
										'border border-gray-200 rounded-lg',
										draggedIndex === index ? 'opacity-50' : '',
									)}
								>
									{/* Step header - always visible */}
									<div className="flex items-center gap-3 px-3 py-2 bg-gray-50">
										{/* Drag handle */}
										<div className="flex-shrink-0 cursor-grab text-gray-400 hover:text-gray-600">
											<svg
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											>
												<circle
													cx="9"
													cy="5"
													r="1"
													fill="currentColor"
												></circle>
												<circle
													cx="9"
													cy="12"
													r="1"
													fill="currentColor"
												></circle>
												<circle
													cx="9"
													cy="19"
													r="1"
													fill="currentColor"
												></circle>
												<circle
													cx="15"
													cy="5"
													r="1"
													fill="currentColor"
												></circle>
												<circle
													cx="15"
													cy="12"
													r="1"
													fill="currentColor"
												></circle>
												<circle
													cx="15"
													cy="19"
													r="1"
													fill="currentColor"
												></circle>
											</svg>
										</div>

										{/* Step number and indicators */}
										<span className="flex-shrink-0 flex items-center gap-1.5 text-sm font-medium text-gray-700">
											Step {index + 1}
											{step.requiresInput && (
												<PencilSquareIcon className="ml-1.5 w-4 h-4 text-gray-400" />
											)}
											{(step.attachments?.length > 0 ||
												hasImage(step.content)) && (
												<PhotoIcon className="ml-1.5 w-4 h-4 text-gray-400" />
											)}
										</span>

										{/* Expand/Collapse button */}
										<button
											type="button"
											onClick={() => toggleStepExpanded(index)}
											className="flex-1 flex items-center justify-between text-left text-sm text-gray-500 hover:text-gray-700"
										>
											<span className="truncate">
												{step.content ? (
													getFirstLine(step.content)
												) : (
													<span className="italic text-gray-400">
														No content
													</span>
												)}
											</span>
											{expandedSteps[index] ? (
												<ChevronUpIcon className="w-4 h-4 flex-shrink-0" />
											) : (
												<ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
											)}
										</button>

										{/* Delete button */}
										<button
											type="button"
											onClick={() => removeStep(index)}
											className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 transition-colors"
										>
											<TrashIcon className="w-5 h-5" />
										</button>
									</div>

									{/* Step content editor - expandable */}
									{expandedSteps[index] && (
										<div className="p-3 border-t border-gray-200">
											<MarkdownEditor
												value={step.content}
												onChange={(content) => updateStep(index, content)}
												placeholder="Enter step description with formatting..."
												compact
												disableImages
											/>
											<StepAttachments
												attachments={step.attachments || []}
												onAttachmentsChange={(attachments) =>
													updateStepAttachments(index, attachments)
												}
												maxFiles={10}
											/>
											{/* Requires Input Toggle */}
											<div className="mt-3 pt-3 border-t border-gray-100">
												<label className="flex items-center gap-2 cursor-pointer">
													<input
														type="checkbox"
														checked={step.requiresInput || false}
														onChange={() => toggleStepRequiresInput(index)}
														className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
													/>
													<span className="text-sm text-gray-700">
														Requires user input
													</span>
													<span className="text-xs text-gray-400">
														— User must provide a response when executing this
														step
													</span>
												</label>
											</div>
										</div>
									)}
								</div>
							))}
						</div>

						{/* Add Step button */}
						<button
							type="button"
							onClick={addStep}
							className="mt-4 w-full flex items-center justify-center gap-2 py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
						>
							<PlusIcon className="w-4 h-4" />
							Add Step
						</button>
					</div>

					{/* Actions */}
					<div className="flex justify-between items-center pt-4 border-t border-gray-200">
						{/* Left side - Delete button (only when editing) */}
						<div>
							{isEditing && (
								<button
									type="button"
									onClick={handleDelete}
									className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 shadow-sm hover:bg-red-50"
								>
									<TrashIcon className="w-4 h-4" />
									Delete SOP
								</button>
							)}
						</div>

						{/* Right side - Cancel and Save */}
						<div className="flex gap-3">
							<button
								type="button"
								onClick={handleClose}
								className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={
									!form.name.trim() ||
									isSubmitting ||
									(isEditing && !hasChanges)
								}
								className={classNames(
									'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
									!form.name.trim() ||
										isSubmitting ||
										(isEditing && !hasChanges)
										? 'bg-gray-400 cursor-not-allowed'
										: 'bg-indigo-600 hover:bg-indigo-700',
								)}
							>
								{isSubmitting
									? 'Saving...'
									: isEditing
										? 'Save Draft'
										: 'Create SOP'}
							</button>

							{isEditing && (
								<button
									type="button"
									onClick={handlePublish}
									disabled={isSubmitting || !canPublish}
									className={classNames(
										'inline-flex items-center rounded-md px-4 py-2 text-sm font-medium shadow-sm',
										isSubmitting || !canPublish
											? 'bg-gray-100 text-gray-400 cursor-not-allowed'
											: 'bg-green-600 text-white hover:bg-green-700',
									)}
								>
									Approve Version
								</button>
							)}
						</div>
					</div>
				</form>
			)}

			{/* Version View Modal - Stacked on top */}
			<StackableModal
				open={!!viewingVersion}
				onClose={() => setViewingVersion(null)}
				title={
					viewingVersion
						? `Version ${formatVersion(viewingVersion.versionMajor, viewingVersion.versionMinor)}`
						: 'View Version'
				}
				size="2xl"
			>
				{viewingVersion && (
					<div className="space-y-4">
						<div className="border-b border-gray-200 pb-4">
							<h3 className="font-medium text-gray-900 text-lg">
								{viewingVersion.name}
							</h3>
							<p className="text-sm text-gray-500 mt-1">
								Published{' '}
								{viewingVersion.publishedAt
									? new Date(viewingVersion.publishedAt).toLocaleDateString(
											'en-US',
											{
												year: 'numeric',
												month: 'short',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit',
											},
										)
									: 'N/A'}
							</p>
						</div>

						{viewingVersion.description && (
							<div>
								<h4 className="text-sm font-medium text-gray-700 mb-2">
									Description
								</h4>
								<div className="bg-gray-50 rounded-lg p-3">
									<MarkdownOrHtmlDisplay
										content={viewingVersion.description}
										className="text-gray-600"
									/>
								</div>
							</div>
						)}

						{viewingVersion.steps?.length > 0 && (
							<div>
								<h4 className="text-sm font-medium text-gray-700 mb-2">
									Checklist Steps ({viewingVersion.steps.length})
								</h4>
								<ol className="space-y-2">
									{viewingVersion.steps.map((step, idx) => (
										<li
											key={idx}
											className="text-sm bg-gray-50 p-3 rounded-lg border border-gray-100"
										>
											<div className="flex items-center gap-2">
												<span className="font-medium text-gray-700">
													Step {idx + 1}:
												</span>
												{step.requiresInput && (
													<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700">
														<PencilSquareIcon className="w-3 h-3" />
														Input Required
													</span>
												)}
											</div>
											<MarkdownOrHtmlDisplay
												content={step.content}
												className="mt-1"
											/>
										</li>
									))}
								</ol>
							</div>
						)}

						<div className="flex justify-end pt-4 border-t border-gray-200">
							<button
								type="button"
								onClick={() => setViewingVersion(null)}
								className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
							>
								Close
							</button>
						</div>
					</div>
				)}
			</StackableModal>
		</StackableModal>
	);
};

export default EditSopModal;
